
import React from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { toast } from '@/hooks/use-toast';

interface UserMenuProps {
  isCollapsed?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ isCollapsed = false }) => {
  const { user, signOut } = useAuth();
  const { data: userProfile } = useUserProfile();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been logged out of your account.",
      });
    } catch (error) {
      toast({
        title: "Sign out failed",
        description: "There was an error signing you out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getInitials = () => {
    if (userProfile?.name) {
      const parts = userProfile.name.trim().split(/\s+/);
      if (parts.length > 1) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
      }
      return parts[0].charAt(0).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const displayName = userProfile?.name || user?.email || 'User';

  return (
    <div className="relative group w-full">
      <div
        className={`flex items-center cursor-pointer px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors ${
          isCollapsed ? 'justify-center' : 'space-x-3'
        }`}
      >
        <div
          className="w-8 h-8 bg-[#A655F8] rounded-full flex items-center justify-center flex-shrink-0"
          title={isCollapsed ? displayName : undefined}
        >
          <span className="text-white font-semibold text-xs">
            {getInitials()}
          </span>
        </div>
        {!isCollapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {displayName}
            </p>
          </div>
        )}
      </div>

      {/* Dropdown menu */}
      <div
        className={`absolute mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ${
          isCollapsed ? 'left-full top-0 ml-2 w-40' : 'top-full left-0 right-0'
        }`}
      >
        <button
          id="user-menu-signout-btn"
          onClick={handleSignOut}
          className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
        >
          <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
          Sign out
        </button>
      </div>
    </div>
  );
};

export default UserMenu;
